import { PromotionInterface } from '@/models/shop/Promotion.types'
import { ArenaProductVariant } from '@/models/shop/ArenaProduct.types'
import { PromotionCartItem } from '@/store/modules/cart'
import { COIN_TYPES } from '@/constants/coins'
import type { CurrencyInterface } from '@/models/shop/Currency'
import { isFixedPricePromotion } from '@/utils/promotions'

export const getReducedPrice = (price: string, reduction: string): string => {
  const originalPrice = Number(price)
  const reductionPercent = Number(reduction)

  if (
    Number.isNaN(originalPrice) ||
    Number.isNaN(reductionPercent) ||
    reductionPercent < 0 ||
    reductionPercent > 100
  ) {
    return price
  }

  const reduced = originalPrice * (1 - reductionPercent / 100)
  return Number.isFinite(reduced) ? reduced.toFixed(2) : price
}

export const getReductionPercentage = (
  price: number | string,
  reduced: number | string,
): string => {
  const originalPrice = Number(price)
  const reducedPrice = Number(reduced)

  if (Number.isNaN(reducedPrice)) {
    return '0'
  } else if (Number.isNaN(originalPrice)) {
    return '100'
  }

  if (originalPrice === 0) {
    return '100'
  }

  const discountPercentage =
    ((originalPrice - reducedPrice) / originalPrice) * 100
  return Math.round(discountPercentage).toFixed(0)
}

export const roundPrice = (price: number | string): number => {
  return Math.round(Number(price) * 100) / 100
}

export const formatPrice = (price: number | string): string => {
  const num = Number(price)
  if (Number.isNaN(num)) return '0.00'
  return num.toFixed(2)
}

const isGroupCoins = (
  group: PromotionInterface['groups'][0],
  variants: ArenaProductVariant[],
): boolean => {
  if (!group?.variants?.length || !variants.length) return false
  const groupVariantIds = new Set(
    group.variants.map((gv) => gv.arena_variant_id),
  )
  return variants.some(
    (variant) =>
      groupVariantIds.has(variant.id) &&
      variant?.attributes?.type === COIN_TYPES.coins,
  )
}

export const sumSelectedVariantPrices = (
  promotion: PromotionInterface,
  groupSelections: PromotionCartItem['groupSelections'] | undefined,
  variants: ArenaProductVariant[],
  getVariantPriceFn: (variant: ArenaProductVariant) => number,
  arenaCurrency?: CurrencyInterface,
): number => {
  if (!promotion?.groups?.length || !groupSelections) return 0

  return promotion.groups.reduce((sum, group) => {
    const selections = groupSelections[group.id]
    if (!selections) return sum

    const isCoins = isGroupCoins(group, variants)

    if (isCoins) {
      // For coins groups, use group.quantity instead of counting selections
      const coinsPerCurrency = Number(arenaCurrency?.coins_per_currency) || 0
      if (coinsPerCurrency > 0) {
        const quantity = Number(group.quantity) || 1
        return sum + quantity / coinsPerCurrency
      }
      return sum
    }

    // For non-coins groups, sum prices of all selected variants
    return selections.reduce((groupSum, selection) => {
      if (!selection?.id) return groupSum

      const variant = variants.find((it) => it.id === selection.id)
      if (!variant) return groupSum

      return groupSum + (Number(getVariantPriceFn(variant)) || 0)
    }, sum)
  }, 0)
}

export const calculatePromotionPriceFromSelections = (
  promotion: PromotionInterface,
  groupSelections: PromotionCartItem['groupSelections'] | undefined,
  variants: ArenaProductVariant[],
  getVariantPriceFn: (variant: ArenaProductVariant) => number,
  arenaCurrency?: CurrencyInterface,
): number => {
  const totalPrice = sumSelectedVariantPrices(
    promotion,
    groupSelections,
    variants,
    getVariantPriceFn,
    arenaCurrency,
  )

  if (isFixedPricePromotion(promotion)) {
    return Number(promotion.discount_value)
  }

  if (totalPrice <= 0) return 0

  const discountPercentage = Number(promotion.discount_value) || 0
  return roundPrice(totalPrice * (1 - discountPercentage / 100))
}
